import React, { useState } from 'react';
import type { AdminTransferDetail } from '@platform/shared-types';
import { businessTransferApi } from '../../../services/businessTransferApi';
import { toastService } from '../../../services/toastService';

type P = { detail: AdminTransferDetail; canAct: boolean; onChange: (d: AdminTransferDetail) => void };
type Step = NonNullable<AdminTransferDetail['transfer']['run']>['steps'][number];

/** F1: warnings a step raised (`ctx.warn`) — money/ops items a human must handle before completion. */
export const stepWarnings = (s: Step): Array<{ code: string; message: string }> =>
  (((s.output as any)?.warnings ?? []) as any[]).map((w) => ({ code: String(w?.code ?? ''), message: String(w?.message ?? '') }));

export const runWarningsOf = (detail: AdminTransferDetail) =>
  (detail.transfer.run?.steps ?? []).flatMap((s) => stepWarnings(s).map((w) => ({ ...w, stepId: s.stepId, step: s.label })));
const CHIP = { done: 'bg-emerald-100 text-emerald-800', running: 'bg-amber-100 text-amber-800', failed: 'bg-red-100 text-red-800', pending: 'bg-stone-100 text-stone-600', skipped: 'bg-stone-100 text-stone-400' } as const;

export default function RunLogPanel({ detail, canAct, onChange }: P) {
  const r = detail.transfer.run;
  const [busy, setBusy] = useState(false);
  if (!r) return <div className="text-sm text-stone-600">Not started. Schedule it in the step above.</div>;
  return (
    <div className="flex flex-col gap-2 text-sm">
      <table className="w-full"><tbody>
        {r.steps.map((s) => (
          <tr key={s.stepId} className="border-t border-stone-100 align-top">
            <td className="py-1.5 pr-3"><span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${CHIP[s.status]}`}>{s.status}</span></td>
            <td className="py-1.5">{s.label}{s.error && <div className="text-xs text-red-700">{s.error}</div>}
              {s.counts && <div className="text-xs text-stone-500">{Object.entries(s.counts).map(([k, v]) => `${k} ${v}`).join(' · ')}</div>}
              {stepWarnings(s).map((w, i) => (
                <div key={`${w.code}-${i}`} role="note" className="mt-1 rounded-md border border-amber-300 border-l-4 border-l-[#DC143C] bg-amber-50 px-2 py-1 text-xs text-amber-900">
                  <span className="font-mono font-semibold">{w.code}</span> — {w.message}
                </div>
              ))}</td>
            <td className="py-1.5 text-right font-mono text-xs text-stone-500">{s.finishedAt && s.startedAt ? `${Math.max(1, Math.round((s.finishedAt - s.startedAt) / 1000))}s` : ''}</td>
          </tr>
        ))}
      </tbody></table>
      {r.status === 'failed' && canAct && (
        <button type="button" disabled={busy} className="min-h-[44px] self-start rounded-lg bg-[#DC143C] px-4 font-semibold text-white"
          onClick={async () => {
            setBusy(true);
            try { onChange(await businessTransferApi.resume(detail.transfer.transferId)); toastService.success('Resumed from the failed step'); }
            catch (e: any) { toastService.error(e.message); } finally { setBusy(false); }
          }}>
          Resume from failed step
        </button>
      )}
    </div>
  );
}
